import { Timer } from "lucide-react";

import type { QuestionClock } from "@/hooks/use-question-clock";
import { cn } from "@/lib/utils";

// Below this share of the limit the bar turns urgent. Not a second timer — the
// colour is the only warning a child gets that the seconds are nearly gone, and
// on a ten-second limit a warning at the halfway point is on screen longer than
// it is useful.
const URGENT_AT = 0.3;

/**
 * The countdown on a question whose skill IS speed.
 *
 * Visible on purpose. A hidden clock cannot train anyone to be faster, and a
 * grade that drops for a reason the child never saw is a secret penalty — the
 * bar is what makes PARTIAL honest here rather than mysterious.
 *
 * Nothing about it locks or submits. At zero it stops and says so, and the
 * child answers at their own pace: the alternative marks a child wrong, for a
 * fact they knew, because they were mid-keystroke.
 */
export function QuestionClockBar({ clock }: { clock: QuestionClock }) {
  // Voided means they asked for help, which stops this being a fluency question
  // at all. Showing an abandoned clock would say the opposite.
  if (clock.limit === null || clock.voided || clock.remaining === null) return null;

  const share = Math.max(0, Math.min(1, clock.remaining / clock.limit));
  const urgent = !clock.expired && share <= URGENT_AT;

  return (
    <div
      className="mt-2 flex items-center gap-2"
      // The bar is decoration for the count beside it, which is the thing worth
      // announcing — and announcing every tick would make a screen reader talk
      // over the question.
      role="timer"
      aria-live="off"
    >
      <Timer
        className={cn(
          "h-4 w-4 shrink-0",
          clock.expired ? "text-muted-foreground" : urgent ? "text-amber-600" : "text-muted-foreground"
        )}
        aria-hidden
      />

      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full",
            // Width only — a transition on a colour change would lag the one
            // moment the colour is meant to be noticed.
            "transition-[width] duration-1000 ease-linear motion-reduce:transition-none",
            clock.expired ? "bg-muted-foreground/40" : urgent ? "bg-amber-500" : "bg-primary"
          )}
          style={{ width: `${share * 100}%` }}
        />
      </div>

      <span
        className={cn(
          "w-20 shrink-0 text-right text-xs tabular-nums",
          clock.expired ? "text-muted-foreground" : urgent ? "font-medium text-amber-600" : "text-muted-foreground"
        )}
      >
        {clock.expired ? "laikas baigėsi" : `${Math.ceil(clock.remaining)} s`}
      </span>
    </div>
  );
}
